import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import createGlobe from 'cobe'
import { LINKS } from '../lib/links'
import { useT, useLocale } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

/**
 * Hero « Aube logistique (Planetrise) » — concept gagnant de l'atelier design
 * (3 concepts, jury unanime). La Terre est une planète massive recadrée par le
 * bas du viewport : un horizon courbe traverse 100 % de la largeur (zéro marge
 * morte à toute résolution), couronné d'une atmosphère bleue et d'un point
 * d'aube au-dessus de la Belgique, d'où les routes logistiques montent dans un
 * ciel étoilé où vit le texte. Le hero occupe exactement 100dvh.
 *
 * Ingénierie : les routes sont de la géométrie 2D exacte (arcs du cercle
 * d'horizon + béziers écran) — aucune synchronisation 3D à calibrer. cobe ne
 * fournit que la texture de continents de la calotte (desktop uniquement ;
 * mobile = arc CSS pur). Une seule boucle rAF, mise en pause hors écran.
 */

/* ─── Constantes de calibration (point unique de réglage) ─── */
const APEX_DVH = 0.79 // position verticale de l'horizon (desktop)
const APEX_DVH_MOBILE = 0.775
const PLANET_K = 0.8 // fraction du canvas cobe occupée par la sphère
const COBE_PHI0 = 0.42 // Belgique face caméra (calibré par captures)
const COBE_THETA = 0.72 // Belgique près du sommet du disque
const COBE_RENDER = 1400 // résolution interne plafonnée, upscale CSS assumé
const SHOW_CONTROL_LABELS = true // micro-label BRU + croix de visée

// Arcs de vol : ils décollent de Bruxelles, montent, et ATTERRISSENT sur
// l'horizon vers d'autres pays (span = longueur d'arc signée le long de la
// courbe, négatif = ouest). Les plus longs sortent de l'écran : le vol
// continue derrière la courbure de la Terre. Hauteur de croisière ∝ distance.
const FLIGHTS = [
  { span: -420, label: 'LON', minVw: 0 },
  { span: -680, label: 'MAD', minVw: 0 },
  { span: -1080, label: null, minVw: 0 },
  // Choix du client : PAR et ROM sur les atterrissages proches du centre,
  // bien en vue (la planète est stylisée, pas une projection géodésique).
  { span: 300, label: 'PAR', minVw: 0 },
  { span: 560, label: 'ROM', minVw: 0 },
  { span: 950, label: null, minVw: 0 },
  { span: -1500, label: null, minVw: 1800 },
  { span: 1400, label: null, minVw: 1800 },
]
const FLIGHTS_MOBILE = [
  { span: -240, label: null, minVw: 0 },
  { span: 170, label: null, minVw: 0 },
  { span: 330, label: null, minVw: 0 },
]
const flightHeight = (span: number) => Math.min(230, 60 + Math.abs(span) * 0.17)

/* ─── Étoiles déterministes (SSR-safe : graine fixe) ─── */
function mulberry32(seed: number) {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
function buildStars(count: number, seed: number, sizes: number[], accentEvery: number) {
  const rnd = mulberry32(seed)
  const shadows: string[] = []
  for (let i = 0; i < count; i++) {
    const x = Math.round(rnd() * 100 * 100) / 100
    const y = Math.round(rnd() * 68 * 100) / 100 // seulement dans le ciel
    const s = sizes[i % sizes.length]
    const accent = accentEvery > 0 && i % accentEvery === 0
    const a = 0.25 + rnd() * 0.35
    const color = accent ? `rgba(188,208,255,${a + 0.15})` : `rgba(255,255,255,${a})`
    shadows.push(`${x}vw ${y}vh 0 ${s > 1 ? `${(s - 1) / 2}px` : '0'} ${color}`)
  }
  return shadows.join(',')
}
const STARS_FAR = buildStars(60, 7, [0], 0)
const STARS_NEAR = buildStars(24, 21, [1, 0.5], 4)

/* ─── Types de scène ─── */
type Scene = {
  vw: number
  vh: number
  mobile: boolean
  ultrawide: boolean
  R: number // rayon du cercle d'horizon en px
  cx: number
  cy: number // centre du cercle (sous le fold)
  apexY: number
  diam: number // diamètre CSS du canvas cobe / de l'arc mobile
}

function computeScene(): Scene {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const mobile = vw < 1024
  const diam = mobile ? vw * 3 : Math.min(Math.max(vw * 1.75, 2200), 4400)
  const R = mobile ? diam / 2 : (diam / 2) * PLANET_K
  const apexY = vh * (mobile ? APEX_DVH_MOBILE : APEX_DVH)
  return { vw, vh, mobile, ultrawide: vw >= 1800, R, cx: vw / 2, cy: apexY + R, apexY, diam }
}

/* ─── Scène planétaire : ciel, routes, planète, atmosphère ─── */
function PlanetScene({ entered }: { entered: boolean }) {
  const reduce = useReducedMotion()
  const [scene, setScene] = useState<Scene | null>(null)
  const cobeRef = useRef<HTMLCanvasElement>(null)
  const routesRef = useRef<HTMLCanvasElement>(null)
  const sectionVisible = useRef(true)

  useEffect(() => {
    const update = () => setScene(computeScene())
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => {
    if (!scene) return
    const routesCanvas = routesRef.current
    if (!routesCanvas) return

    const dprFor = (logical: number) =>
      logical > 1400 ? 1 : Math.min(1.5, window.devicePixelRatio || 1)
    const dpr = dprFor(Math.max(scene.vw, scene.vh))
    routesCanvas.width = scene.vw * dpr
    routesCanvas.height = scene.vh * dpr
    const ctx = routesCanvas.getContext('2d')!

    /* Routes : échantillonnées une fois (LUT), rejouées chaque frame */
    const hubAngle = -Math.PI / 2
    const hub = { x: scene.cx, y: scene.apexY }
    const STEPS = 44

    type Route = {
      pts: { x: number; y: number }[]
      sky: boolean
      dur: number
      offset: number
      prevT: number
    }
    const routes: Route[] = []

    // Arcs de vol vers d'autres pays : décollage du hub, croisière en
    // altitude, atterrissage sur le cercle d'horizon (comme un vrai vol
    // au-dessus de la courbure).
    const flights = (scene.mobile ? FLIGHTS_MOBILE : FLIGHTS).filter(
      (f) => scene.vw >= f.minVw,
    )
    flights.forEach((f, i) => {
      const H = scene.mobile ? Math.min(120, 40 + Math.abs(f.span) * 0.2) : flightHeight(f.span)
      const pts = []
      for (let s = 0; s <= STEPS; s++) {
        const t = s / STEPS
        const a = hubAngle + (f.span * t) / scene.R
        const lift = 3 + H * Math.sin(Math.PI * t)
        pts.push({
          x: scene.cx + (scene.R + lift) * Math.cos(a),
          y: scene.cy + (scene.R + lift) * Math.sin(a),
        })
      }
      routes.push({ pts, sky: true, dur: 7500 + (i % 4) * 1200, offset: 0.35 + i * 0.19, prevT: 0 })
    })

    // Atténuation des routes ciel dans le rectangle du texte
    const inTextZone = (p: { x: number; y: number }) =>
      p.x > scene.vw * 0.25 && p.x < scene.vw * 0.75 && p.y < scene.vh * 0.65

    type Flare = { x: number; y: number; start: number }
    const flares: Flare[] = []
    type Ring = { start: number }
    const rings: Ring[] = []

    const startTime = performance.now()
    let raf = 0
    let running = true

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

    const draw = (now: number) => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, scene.vw, scene.vh)
      // Clamp : le timestamp du premier rAF peut précéder startTime (vsync) ;
      // un elapsed négatif rend `elapsed % 3000` négatif → rayon d'arc négatif
      // → IndexSizeError qui tue la boucle à la première frame.
      const elapsed = Math.max(0, now - startTime)

      routes.forEach((r, i) => {
        // Tracé progressif à l'entrée (t=1400ms + stagger 180ms, 800ms)
        const reveal = reduce
          ? 1
          : Math.min(1, Math.max(0, (elapsed - 1400 - i * 180) / 800))
        if (reveal === 0) return
        const upto = Math.round(easeOut(reveal) * STEPS)

        ctx.lineWidth = scene.ultrawide ? 1.7 : 1.4
        ctx.lineCap = 'round'
        for (let s = 1; s <= upto; s++) {
          const p0 = r.pts[s - 1]
          const p1 = r.pts[s]
          const dim = r.sky && (inTextZone(p0) || inTextZone(p1))
          ctx.strokeStyle = dim ? 'rgba(143,176,255,0.22)' : 'rgba(163,190,255,0.7)'
          ctx.beginPath()
          ctx.moveTo(p0.x, p0.y)
          ctx.lineTo(p1.x, p1.y)
          ctx.stroke()
        }

        // Point de destination
        const end = r.pts[STEPS]
        if (reveal === 1) {
          ctx.fillStyle = 'rgba(163,190,255,0.9)'
          ctx.beginPath()
          ctx.arc(end.x, end.y, 2.2, 0, Math.PI * 2)
          ctx.fill()
        }

        // Comète (colis) : tête + traîne additive
        if (reveal === 1) {
          const t = reduce ? 0.66 : ((elapsed / r.dur + r.offset) % 1)
          if (!reduce && t < r.prevT) {
            rings.push({ start: now }) // départ du hub
            // livraison : flare seulement si l'atterrissage est à l'écran
            if (r.sky && end.x > 0 && end.x < scene.vw && end.y < scene.vh)
              flares.push({ x: end.x, y: end.y, start: now })
          }
          r.prevT = t
          const head = Math.round(t * STEPS)
          ctx.save()
          ctx.globalCompositeOperation = 'lighter'
          // traîne : 8 segments dégressifs derrière la tête
          for (let k = 0; k < 8; k++) {
            const s = head - k
            if (s < 1) break
            const alpha = 0.5 * (1 - k / 8)
            const p0 = r.pts[s - 1]
            const p1 = r.pts[s]
            ctx.strokeStyle = `rgba(143,176,255,${alpha})`
            ctx.lineWidth = 1.6
            ctx.beginPath()
            ctx.moveTo(p0.x, p0.y)
            ctx.lineTo(p1.x, p1.y)
            ctx.stroke()
          }
          const hp = r.pts[head]
          ctx.shadowColor = '#5e84ff'
          ctx.shadowBlur = 10
          ctx.fillStyle = '#cdd9ff'
          ctx.beginPath()
          ctx.arc(hp.x, hp.y, 2.5, 0, Math.PI * 2)
          ctx.fill()
          ctx.restore()
        }
      })

      // Flares de livraison (400ms) et anneaux de départ du hub (900ms)
      for (let i = flares.length - 1; i >= 0; i--) {
        const f = flares[i]
        const p = (now - f.start) / 400
        if (p >= 1) {
          flares.splice(i, 1)
          continue
        }
        ctx.fillStyle = `rgba(205,217,255,${0.9 * (1 - p)})`
        ctx.beginPath()
        ctx.arc(f.x, f.y, 2.2 + 2.2 * 0.8 * p, 0, Math.PI * 2)
        ctx.fill()
      }
      for (let i = rings.length - 1; i >= 0; i--) {
        const p = (now - rings[i].start) / 900
        if (p >= 1) {
          rings.splice(i, 1)
          continue
        }
        ctx.strokeStyle = `rgba(143,176,255,${0.5 * (1 - p)})`
        ctx.lineWidth = 1.2
        ctx.beginPath()
        ctx.arc(hub.x, hub.y, 18 * p, 0, Math.PI * 2)
        ctx.stroke()
      }

      // Ping périodique du hub (3s)
      if (!reduce) {
        const pp = (elapsed % 3000) / 3000
        ctx.strokeStyle = `rgba(143,176,255,${0.45 * (1 - pp)})`
        ctx.lineWidth = 1.2
        ctx.beginPath()
        ctx.arc(hub.x, hub.y, 26 * pp, 0, Math.PI * 2)
        ctx.stroke()
      }
      // Hub lui-même
      ctx.shadowColor = '#5e84ff'
      ctx.shadowBlur = 12
      ctx.fillStyle = '#e3eaff'
      ctx.beginPath()
      ctx.arc(hub.x, hub.y, 3, 0, Math.PI * 2)
      ctx.fill()
      ctx.shadowBlur = 0
    }

    /* cobe — desktop uniquement ; oscillation lente autour de la Belgique
       (une rotation continue sortirait le hub du cadre en quelques secondes) */
    let globe: { destroy: () => void; update: (s: Record<string, number>) => void } | null = null
    if (!scene.mobile && cobeRef.current) {
      globe = createGlobe(cobeRef.current, {
        devicePixelRatio: 1,
        width: COBE_RENDER,
        height: COBE_RENDER,
        phi: COBE_PHI0,
        theta: COBE_THETA,
        dark: 1,
        diffuse: 1.8,
        mapSamples: 32000,
        mapBrightness: 8,
        baseColor: [0.12, 0.14, 0.26],
        markerColor: [0.55, 0.68, 1],
        glowColor: [0.16, 0.22, 0.55],
        opacity: 0.9,
        markers: [],
      })
    }

    const loop = (now: number) => {
      if (running) {
        if (globe && !reduce) {
          const t = (now % 60000) / 60000
          globe.update({ phi: COBE_PHI0 + 0.06 * Math.sin(t * Math.PI * 2) })
        }
        draw(now)
      }
      raf = requestAnimationFrame(loop)
    }
    if (reduce) {
      draw(performance.now())
    } else {
      raf = requestAnimationFrame(loop)
    }

    // Pause hors écran / onglet caché
    const io = new IntersectionObserver(([e]) => {
      sectionVisible.current = e.isIntersecting
      running = e.isIntersecting && !document.hidden
    })
    io.observe(routesCanvas)
    const onVis = () => {
      running = sectionVisible.current && !document.hidden
    }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
      globe?.destroy()
    }
  }, [scene, reduce])

  if (!scene) return null

  const planetStyle = {
    left: scene.cx - scene.diam / 2,
    top: scene.mobile
      ? scene.apexY
      : scene.apexY - (scene.diam / 2) * (1 - PLANET_K),
    width: scene.diam,
    height: scene.diam,
  }
  const rimStyle = {
    left: scene.cx - scene.R,
    top: scene.apexY,
    width: scene.R * 2,
    height: scene.R * 2,
  }

  return (
    <>
      {/* Étoiles (2 profondeurs) */}
      <div aria-hidden className="absolute inset-0 z-0 pointer-events-none">
        <div className="hero-stars-far absolute top-0 left-0 w-px h-px" style={{ boxShadow: STARS_FAR }} />
        <div className="hero-stars-near absolute top-0 left-0 w-px h-px" style={{ boxShadow: STARS_NEAR }} />
        {/* 4 étoiles qui scintillent */}
        {[
          [12, 18],
          [83, 12],
          [68, 38],
          [25, 44],
        ].map(([x, y], i) => (
          <span
            key={i}
            className="hero-star-twinkle absolute w-[2px] h-[2px] rounded-full bg-accent-200"
            style={{ left: `${x}vw`, top: `${y}vh`, animationDelay: `${i * 0.8}s` }}
          />
        ))}
      </div>

      {/* Canvas routes + comètes. Au-dessus de la planète ET de l'atmosphère :
          les arcs volent dans la bande lumineuse de l'horizon, dessous ils se
          noyaient dans le bloom. */}
      <div aria-hidden className="absolute inset-0 z-[35] pointer-events-none">
        <canvas ref={routesRef} className="absolute inset-0 w-full h-full" />
      </div>

      {/* Planète : la calotte monte à l'entrée (geste fondateur de la scène) */}
      <motion.div
        aria-hidden
        initial={reduce ? false : { opacity: 0, y: 90 }}
        animate={entered ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        className="absolute z-20 pointer-events-none"
        style={planetStyle}
      >
        {scene.mobile ? (
          /* Mobile : arc CSS pur, la calotte de 190px est indistinguable de cobe */
          <div className="relative h-full w-full rounded-full overflow-hidden bg-[#0b1030]">
            <div
              className="absolute inset-x-0 top-0 h-[300px]"
              style={{
                background:
                  'linear-gradient(to bottom, rgba(24,32,72,0.9), rgba(11,16,48,0) 100%)',
              }}
            />
            <div
              className="absolute inset-x-0 top-0 h-[260px]"
              style={{
                backgroundImage:
                  'radial-gradient(rgba(160,180,255,0.4) 1px, transparent 1px)',
                backgroundSize: '7px 7px',
                maskImage: 'linear-gradient(to bottom, black, transparent 85%)',
              }}
            />
          </div>
        ) : (
          <canvas ref={cobeRef} className="h-full w-full" />
        )}
      </motion.div>

      {/* Atmosphère : liseré net + arête chaude + bande + bloom */}
      <motion.div
        aria-hidden
        initial={reduce ? false : { opacity: 0 }}
        animate={entered ? { opacity: 1 } : undefined}
        transition={{ duration: 0.7, delay: 1.0 }}
        className="absolute z-30 pointer-events-none"
      >
        <div
          className="absolute rounded-full"
          style={{
            ...rimStyle,
            border: '1.5px solid rgba(150,175,255,0.9)',
            boxShadow:
              '0 -2px 6px rgba(190,208,255,0.45), 0 -10px 44px 6px rgba(88,120,255,0.45), inset 0 14px 60px rgba(59,93,255,0.35)',
          }}
        />
        {/* Bloom d'horizon pleine largeur */}
        <div
          className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
          style={{
            top: scene.apexY,
            width: '130vw',
            height: 340,
            background:
              'radial-gradient(ellipse at center, rgba(59,93,255,0.28), transparent 70%)',
          }}
        />
      </motion.div>

      {/* Point d'aube au-dessus de la Belgique */}
      <motion.div
        aria-hidden
        initial={reduce ? false : { opacity: 0, scaleX: 0.6 }}
        animate={entered ? { opacity: 1, scaleX: 1 } : undefined}
        transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
        className="absolute z-30 pointer-events-none hero-dawn"
        style={{
          left: scene.cx - 280,
          top: scene.apexY - 75,
          width: 560,
          height: 150,
          transformOrigin: 'bottom center',
          background:
            'radial-gradient(closest-side, rgba(210,222,255,0.85), rgba(120,150,255,0.35) 45%, transparent 75%)',
          filter: 'blur(24px)',
          mixBlendMode: 'screen',
        }}
      />
      {SHOW_CONTROL_LABELS && (
        <motion.div
          aria-hidden
          initial={reduce ? false : { opacity: 0 }}
          animate={entered ? { opacity: 1 } : undefined}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="absolute z-30 pointer-events-none text-center"
          style={{ left: scene.cx - 40, top: scene.apexY + 16, width: 80 }}
        >
          <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/40">
            BRU
          </span>
        </motion.div>
      )}
      {/* Codes des destinations posés sur l'horizon, aux atterrissages visibles */}
      {SHOW_CONTROL_LABELS &&
        !scene.mobile &&
        FLIGHTS.filter((f) => f.label && scene.vw >= f.minVw).map((f) => {
          const a = -Math.PI / 2 + f.span / scene.R
          const x = scene.cx + scene.R * Math.cos(a)
          const y = scene.cy + scene.R * Math.sin(a)
          if (y > scene.vh - 28) return null
          return (
            <motion.span
              key={f.label}
              aria-hidden
              initial={reduce ? false : { opacity: 0 }}
              animate={entered ? { opacity: 1 } : undefined}
              transition={{ duration: 0.6, delay: 1.9 }}
              className="absolute z-30 pointer-events-none text-[10px] font-medium uppercase tracking-[0.18em] text-white/40"
              style={{ left: x - 24, top: y + 14, width: 48, textAlign: 'center' }}
            >
              {f.label}
            </motion.span>
          )
        })}
    </>
  )
}

/* ─── Texte (copy figée, H1 en CSS pur = LCP) ─── */

type Segment = { text: string; className?: string; gradient?: boolean }

function RevealLine({ segments, delayClass = 'hero-delay-line-1' }: { segments: Segment[]; delayClass?: string }) {
  return (
    <span className={`inline-block hero-line-reveal ${delayClass}`}>
      {segments.map((seg, i) => (
        <span key={i}>
          <span
            className={
              seg.gradient
                ? `${seg.className ?? ''} inline-block align-baseline bg-gradient-to-r from-accent-300 via-accent-400 to-accent-500 bg-clip-text text-transparent pb-[0.14em] pr-[0.04em] [filter:drop-shadow(0_0_28px_rgba(59,93,255,0.4))]`
                : (seg.className ?? '')
            }
          >
            {seg.text}
          </span>
          {i < segments.length - 1 ? ' ' : ''}
        </span>
      ))}
    </span>
  )
}

const TRUST_ICONS: Record<string, ReactNode> = {
  trustDelivery: (
    <>
      <path d="M3 7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v9H3z" />
      <path d="M15 9h3.5a1 1 0 0 1 .82.43l1.5 2.14a1 1 0 0 1 .18.57V16h-6z" />
      <circle cx="7" cy="17.5" r="1.5" />
      <circle cx="17" cy="17.5" r="1.5" />
    </>
  ),
  trustAllLevels: (
    <>
      <path d="M16 19v-1a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v1" />
      <circle cx="9.5" cy="8" r="3" />
      <path d="M21 19v-1a3 3 0 0 0-2.25-2.9" />
      <path d="M15.5 5.2a3 3 0 0 1 0 5.6" />
    </>
  ),
  trustNoCommitment: (
    <>
      <rect x="4" y="10.5" width="16" height="9.5" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 7.5-1.9" />
      <circle cx="12" cy="15" r="1.2" />
    </>
  ),
  trustReturns: (
    <>
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 4v4h-4" />
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 20v-4h4" />
    </>
  ),
}

const FADE = (reduce: boolean, delay: number, y = 12) => ({
  initial: reduce ? false : { opacity: 0, y },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] as const },
})

export default function HeroGlobe() {
  const { t } = useT()
  const locale = useLocale()
  const reduce = useReducedMotion()
  const [entered, setEntered] = useState(false)
  useEffect(() => setEntered(true), [])

  return (
    <section className="hero-night relative h-[100dvh] min-h-[640px] overflow-hidden bg-[#06060a]">
      {/* L1 — Espace profond : le bas se réchauffe à l'approche de la planète */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, #06060a 0%, #0a0d1e 55%, #0d1226 100%)',
        }}
      />
      {/* Dot grid — uniquement le haut du ciel */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at 50% 20%, black 20%, transparent 65%)',
        }}
      />
      {/* Nébuleuse derrière le H1 */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          top: '10%',
          width: 900,
          height: 480,
          background:
            'radial-gradient(ellipse at center, rgba(59,93,255,0.10), transparent 70%)',
        }}
      />

      <PlanetScene entered={entered} />

      {/* L8 — Contenu, centré dans le ciel. pt mobile ≥ hauteur de la nav
          flottante pour que le badge ne glisse jamais dessous. */}
      <div className="relative z-40 h-full flex flex-col items-center justify-center text-center px-5 sm:px-6 pb-[19dvh] lg:pb-[17dvh] pt-[92px] sm:pt-16">
        {/* Badge à bordure dégradée */}
        <motion.div {...FADE(!!reduce, 0.15, -8)} className="hero-fade-up">
          <span className="inline-block rounded-full p-px bg-gradient-to-r from-accent-400/40 via-white/10 to-accent-400/20">
            <span className="flex items-center gap-2 rounded-full bg-[#0a0d1e]/90 backdrop-blur-sm px-3 py-1 text-[11px] sm:text-xs">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400" />
              </span>
              <span className="font-medium text-accent-200">{t('hero.badgeAutopilot')}</span>
              {/* Second segment masqué sur mobile : badge sur une seule ligne courte */}
              <span className="hidden sm:inline text-accent-400/60">·</span>
              <span className="hidden sm:inline text-accent-300">{t('hero.badgeWarehouse')}</span>
            </span>
          </span>
        </motion.div>

        {/* H1 — copy figée, animation CSS pure (LCP) */}
        {/* Taille aussi plafonnée par la hauteur du viewport (9vh) : sur un
            13 pouces peu haut le titre se tempère, sur un 27 pouces rien ne
            change (le plafond 4.75rem s'applique avant). */}
        <h1 className="hero-headline-safe mt-[clamp(1rem,2.6vh,2rem)] text-balance font-semibold tracking-[-0.03em] text-[#f7f7f8] text-[clamp(2.15rem,min(1.1rem_+_4.9vw,9vh),4.75rem)] leading-[1.08]">
          <RevealLine
            segments={[
              { text: t('hero.headlineLine1Part1') },
              { text: t('hero.headlineLine1Part2'), gradient: true, className: 'font-semibold font-normal' },
              { text: t('hero.headlineLine1Part3'), className: 'whitespace-nowrap' },
            ]}
            delayClass="hero-delay-line-1"
          />
          <br />
          <RevealLine
            segments={[
              { text: t('hero.headlineLine2Part1'), gradient: true, className: 'font-semibold font-normal' },
              { text: t('hero.headlineLine2Part2') },
              { text: t('hero.headlineLine2Part3'), gradient: true, className: 'font-semibold font-normal' },
            ]}
            delayClass="hero-delay-line-2"
          />
        </h1>

        {/* Sous-titre masqué sur mobile (mur de 5 lignes sur iPhone) — il
            reste dans le DOM prérendu : zéro impact SEO. */}
        <motion.p
          {...FADE(!!reduce, 0.35)}
          className="hidden sm:block mt-[clamp(0.9rem,2.2vh,1.5rem)] max-w-[620px] text-ink-300 text-[clamp(0.9375rem,1.05vw,1.0625rem)] leading-[1.65]"
        >
          {t('hero.subhead')}
        </motion.p>

        {/* CTAs */}
        <motion.div
          {...FADE(!!reduce, 0.5, 14)}
          className="mt-[clamp(1.1rem,3vh,2rem)] flex flex-col sm:flex-row items-center justify-center gap-3 w-full sm:w-auto max-w-[340px] sm:max-w-none"
        >
          <Link
            to={`${localizedPath('contact', locale)}#rendez-vous`}
            className="relative group inline-flex w-full sm:w-auto items-center justify-center gap-2 bg-accent-600 hover:bg-accent-500 text-white rounded-full pl-6 pr-4 py-3.5 text-sm font-medium shadow-[inset_0_1px_0_rgba(255,255,255,0.25),0_0_0_1px_rgba(94,132,255,0.4),0_16px_50px_-12px_rgba(59,93,255,0.8)] transition-[background-color,transform] hover:-translate-y-px active:scale-[0.98] overflow-hidden"
          >
            <span
              aria-hidden
              className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none"
            />
            <span className="relative">{t('hero.ctaPrimary')}</span>
            <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-white/20">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </Link>
          <Link
            to={localizedPath('pricing', locale)}
            className="inline-flex w-full sm:w-auto items-center justify-center rounded-full border border-white/15 bg-white/[0.06] backdrop-blur-sm px-6 py-3.5 text-sm font-medium text-white transition-[border-color,transform] hover:border-white/35 hover:-translate-y-px active:scale-[0.98]"
          >
            {t('hero.ctaSecondary')}
          </Link>
        </motion.div>

        <motion.p {...FADE(!!reduce, 0.65, 6)} className="mt-[clamp(0.7rem,1.8vh,1.1rem)] text-xs font-medium text-ink-300">
          {t('hero.ctaReassurance')}
        </motion.p>

        {/* Pills en verre, posées dans le bloom de l'atmosphère */}
        <motion.div
          {...FADE(!!reduce, 0.75, 10)}
          className="mt-[clamp(1.2rem,3.4vh,2.4rem)] flex flex-wrap lg:flex-nowrap items-center justify-center gap-2 sm:gap-2.5 text-[11px] sm:text-xs text-ink-200 max-w-[680px] lg:max-w-none"
        >
          {(
            ['trustDelivery', 'trustAllLevels', 'trustNoCommitment', 'trustReturns'] as const
          ).map((key) => (
            <span
              key={key}
              /* Mobile : seules les 2 pills courtes (livraison, sans engagement)
                 restent — une seule ligne aérée au lieu de 3 rangées sur la planète */
              className={`${
                key === 'trustAllLevels' || key === 'trustReturns' ? 'hidden sm:inline-flex' : 'inline-flex'
              } items-center gap-2 whitespace-nowrap rounded-full border border-white/10 border-b-accent-400/25 bg-white/[0.06] backdrop-blur-sm px-3 py-1.5 sm:px-3.5 sm:py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]`}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="shrink-0 text-accent-400"
              >
                {TRUST_ICONS[key]}
              </svg>
              {t(`hero.${key}`)}
            </span>
          ))}
        </motion.div>
      </div>

      {/* L9 — Craft : grain film + vignette asymétrique */}
      <div
        aria-hidden
        className="absolute inset-0 z-50 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundSize: '180px 180px',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 z-50 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 120% 90% at 50% 30%, transparent 55%, rgba(3,3,9,0.4) 100%), linear-gradient(to top, rgba(3,3,9,0.35), transparent 30%)',
        }}
      />
    </section>
  )
}
